import type { SweepstakesAnswerKey } from "@/utils/constants";
import type {
  PopupGift,
  PopupOffer,
  StandaloneGift,
} from "@/utils/popupOffers";

/**
 * Lines render with a <br> between them. A `breakFrom` of "2xl" breaks only at
 * that width and up, matching the original success headline.
 */
export type Lines = { lines: string[]; breakFrom?: "2xl" };

export type QuizOptionCopy = { key: SweepstakesAnswerKey; label: string };

export type PopupCopy = {
  a11y: { dialog: string; close: string };
  intro: { eyebrow: string; headline: string; body?: Lines; cta: string };
  quiz: {
    subhead: string;
    body: string;
    options: QuizOptionCopy[];
    skip: string;
  };
  email: { subhead: string; body: Lines; cta: string };
  success: {
    eyebrow: string;
    headline: Lines;
    body: Lines;
    /** Gift offers show the code with this lead-in; the draw shows none. */
    codeLead?: string;
    codeNote?: string;
    cta: string;
  };
};

export const EMAIL_PRIVACY = {
  lead: "By signing up, you accept our",
  link: "Privacy Policy",
};

export const GIFT_BADGE_ALT: Record<PopupGift, string> = {
  bag_pass: "Annual Bag Pass",
  matic_care: "Matic Care",
  dock: "Extra Dock",
  pet_bundle: "Pet Brush Roll and Annual Bag Pass",
};

export const CODE_COPIED = "Copied!";
export const CODE_COPY_LABEL = "Copy code";

const HEADACHE_OPTIONS: QuizOptionCopy[] = [
  { key: "endless_pet_hair", label: "Endless Pet Hair" },
  { key: "less_kids", label: "Kids. Say Less." },
  { key: "hate_vacuuming", label: "I just hate vacuuming" },
  { key: "hate_mopping", label: "Mopping is an ordeal" },
];

const HEADACHE_QUIZ = {
  subhead: "We just want to know…",
  body: "What's your biggest cleaning headache?",
  options: HEADACHE_OPTIONS,
  skip: "skip",
};

/** Variants A and B: today's $500 sweepstakes, copy unchanged. */
export const SWEEPSTAKES_COPY: PopupCopy = {
  a11y: { dialog: "Sweepstakes", close: "Close sweepstakes" },
  intro: {
    eyebrow: "Fall sweepstakes",
    headline: "Want to enter to win $500 off?",
    cta: "Of course",
  },
  quiz: HEADACHE_QUIZ,
  email: {
    subhead: "It could be your lucky day!",
    body: { lines: ["Pop in your email below.", "We promise it's worth it."] },
    cta: "Enter to win $500",
  },
  success: {
    eyebrow: "Fall sweepstakes",
    headline: { lines: ["Nice! We'll be", "in touch!"], breakFrom: "2xl" },
    body: {
      lines: [
        "No need to wait to bring Matic home.",
        "If you win, we'll refund $500 from your purchase.",
      ],
    },
    cta: "Shop",
  },
};

const GIFT_CODE_LEAD =
  "Add Matic and your gift to your cart, then enter this code at checkout:";
const GIFT_CODE_NOTE = "We’ve emailed your code too.";

/** Variant C. */
export const BAG_PASS_COPY: PopupCopy = {
  a11y: { dialog: "Gift offer", close: "Close gift offer" },
  intro: {
    eyebrow: "A gift with your Matic",
    headline: "Want a free Annual Bag Pass?",
    body: {
      lines: ["A year of HEPA bags, a $120 value,", "free with your Matic."],
    },
    cta: "Yes, please",
  },
  quiz: HEADACHE_QUIZ,
  email: {
    subhead: "Where should we send it?",
    body: { lines: ["Enter your email and we’ll send your gift code."] },
    cta: "Get my free Bag Pass",
  },
  success: {
    eyebrow: "Your gift",
    headline: { lines: ["Your Bag Pass", "is on us!"] },
    body: { lines: ["A year of HEPA bags, free with your Matic."] },
    codeLead: GIFT_CODE_LEAD,
    codeNote: GIFT_CODE_NOTE,
    cta: "Shop Matic",
  },
};

/** Variant D. */
export const MATIC_CARE_COPY: PopupCopy = {
  a11y: { dialog: "Gift offer", close: "Close gift offer" },
  intro: {
    eyebrow: "A gift with your Matic",
    headline: "Want Matic Care, free?",
    body: {
      lines: [
        "Two years of warranty plus accidental damage coverage, a $180 value, free with your Matic.",
      ],
    },
    cta: "Yes, please",
  },
  quiz: HEADACHE_QUIZ,
  email: {
    subhead: "Where should we send it?",
    body: { lines: ["Enter your email and we’ll send your gift code."] },
    cta: "Get my free Matic Care",
  },
  success: {
    eyebrow: "Your gift",
    headline: { lines: ["Matic Care", "is on us!"] },
    body: {
      lines: ["Two years of warranty and accidental damage coverage."],
    },
    codeLead: GIFT_CODE_LEAD,
    codeNote: GIFT_CODE_NOTE,
    cta: "Shop Matic",
  },
};

/** Variant E's success screen depends on the gift its quiz answer earned. */
const QUIZ_SUCCESS: Record<
  PopupGift,
  Pick<PopupCopy["success"], "headline" | "body">
> = {
  pet_bundle: {
    headline: { lines: ["Pet hair,", "handled."] },
    body: {
      lines: [
        "A felt-lined Pet Brush Roll and a year of HEPA bags, a $155 value, free with your Matic.",
      ],
    },
  },
  matic_care: {
    headline: { lines: ["Kid-proof", "your Matic."] },
    body: {
      lines: [
        "Matic Care: two years of warranty and accidental damage coverage, a $180 value, free with your Matic.",
      ],
    },
  },
  dock: {
    headline: { lines: ["An extra dock,", "on us."] },
    body: {
      lines: [
        "Matic can’t climb stairs, so here’s a second dock, a $98 value, free with your Matic.",
      ],
    },
  },
  bag_pass: {
    headline: { lines: ["A year of bags,", "on us."] },
    body: {
      lines: ["An Annual Bag Pass, a $120 value,", "free with your Matic."],
    },
  },
};

/** Variant E. Same question as today, plus "Multiple floors". */
export const quizCopy = (gift: PopupGift): PopupCopy => ({
  a11y: { dialog: "Gift offer", close: "Close gift offer" },
  intro: {
    eyebrow: "A gift with your Matic",
    headline: "Want a free gift with your Matic?",
    body: {
      lines: [
        "Tell us what makes cleaning a chore, and we'll find something to help.",
      ],
    },
    cta: "Let’s go",
  },
  quiz: {
    ...HEADACHE_QUIZ,
    options: [
      { key: "endless_pet_hair", label: "Endless Pet Hair" },
      { key: "less_kids", label: "Kids. Say Less." },
      { key: "multiple_floors", label: "Multiple floors" },
      { key: "hate_mopping", label: "Mopping is an ordeal" },
      { key: "hate_vacuuming", label: "I just hate vacuuming" },
    ],
  },
  email: {
    subhead: "Your gift is ready!",
    body: { lines: ["Pop in your email", "to see what you got."] },
    cta: "Reveal my gift",
  },
  success: {
    eyebrow: "Your gift",
    ...QUIZ_SUCCESS[gift],
    codeLead: GIFT_CODE_LEAD,
    codeNote: GIFT_CODE_NOTE,
    cta: "Shop Matic",
  },
});

const STANDALONE_GIFT_COPY: Record<StandaloneGift, PopupCopy> = {
  bag_pass: BAG_PASS_COPY,
  matic_care: MATIC_CARE_COPY,
};

export const popupCopy = (
  offer: PopupOffer,
  gift: PopupGift | null,
): PopupCopy => {
  switch (offer.kind) {
    case "sweepstakes":
      return SWEEPSTAKES_COPY;
    case "gift":
      return STANDALONE_GIFT_COPY[offer.gift];
    case "quiz":
      return quizCopy(gift ?? "bag_pass");
  }
};
